/* ===================================================================
 * coupert-suppress.draft.js  -  DRAFT, NOT YET DEPLOYED
 * -------------------------------------------------------------------
 * Purpose: remove the Coupert browser-extension widget from
 *          nanominerals.co.uk STOREFRONT pages (product/collection/cart).
 *
 * GROUND-TRUTH SIGNATURE (confirmed live, 2026-06-22):
 *   Coupert injects <div data-coupert-react-wrapper-css-ready="1"> at
 *   THREE points:
 *     1. inside <product-info>  -> the visible widget (price <-> shipping)
 *     2. inside <body>          -> a second injection point
 *     3. direct child of <html> -> a ~10k-char CSS-isolation wrapper
 *   All three carry the attribute data-coupert-react-wrapper-css-ready,
 *   so one selector catches them all. The visible content renders into a
 *   React-managed shadow/iframe inside the wrapper; removing the WRAPPER
 *   takes its shadow/iframe with it.
 *
 * HONEST SCOPE - read before deploying:
 *   - Removes Coupert only where OUR theme JS runs = storefront pages.
 *     The product-page widget (point 1) IS such a surface, so this works.
 *   - It does NOT touch Shopify's hosted checkout (shop.app / /checkouts/).
 *     On Basic that surface is unreachable, so Coupert auto-applying a
 *     leaked code AT CHECKOUT is NOT something this can stop. Different
 *     problem, different (declined) fix.
 *   - Cat-and-mouse: if Coupert renames the data-coupert-* attribute the
 *     selector stops matching until updated. Re-derive from DevTools.
 *   - Coupert's content script may RE-INJECT after we remove. We remove
 *     again on each mutation; a hard cap (below) prevents a runaway loop.
 *     Watch for flicker during testing (see DEBUG).
 *
 * Owner approval required before this ships (draft -> approve -> publish).
 * ===================================================================*/
(function () {
  'use strict';

  // Flip to true while testing to log every removal in the console.
  var DEBUG = false;

  // Backstop: if Coupert re-injects in a tight loop, stop after this many
  // total removals and warn, so we never spin the CPU. Tune in testing.
  var MAX_REMOVALS = 1000;

  // Don't run inside iframes (incl. Coupert's own / app frames).
  if (window.top !== window.self) return;

  // Belt-and-braces: never act on checkout/account paths (we can't anyway).
  if (/^\/(checkout|checkouts|wallets|account|\d+\/orders)/.test(location.pathname)) return;

  // Primary signature - the attribute Coupert stamps on every wrapper.
  var SELECTOR = '[data-coupert-react-wrapper-css-ready]';

  var removed = 0;
  var stopped = false;

  function isCoupert(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute && el.hasAttribute('data-coupert-react-wrapper-css-ready')) return true;
    // Resilience: catch any future data-coupert-* attribute too.
    if (el.getAttributeNames) {
      var names = el.getAttributeNames();
      for (var i = 0; i < names.length; i++) {
        if (names[i].indexOf('data-coupert') === 0) return true;
      }
    }
    return false;
  }

  function kill(el, reason) {
    if (!el || !el.isConnected || stopped) return;
    try {
      el.remove();
      removed++;
      if (DEBUG) console.info('[coupert-suppress] removed', reason, '(total ' + removed + ')');
      if (removed >= MAX_REMOVALS) {
        stopped = true;
        if (observer) observer.disconnect();
        console.warn('[coupert-suppress] hit MAX_REMOVALS (' + MAX_REMOVALS +
          ') - likely a re-injection loop; backing off. Consider hiding instead of removing.');
      }
    } catch (e) {
      if (DEBUG) console.warn('[coupert-suppress] remove failed', e);
    }
  }

  function sweep(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var nodes;
    try { nodes = scope.querySelectorAll(SELECTOR); } catch (e) { return; }
    for (var i = 0; i < nodes.length; i++) kill(nodes[i], 'sweep');
  }

  var observer = null;

  function start() {
    sweep(document); // catches anything injected before we attached

    observer = new MutationObserver(function (mutations) {
      if (stopped) return;
      for (var m = 0; m < mutations.length; m++) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (isCoupert(node)) { kill(node, 'mutation:self'); continue; }
          // Coupert wrapper nested inside an added subtree.
          if (node.querySelector) sweep(node);
        }
      }
    });

    // Observe the whole document (point 3 is a child of <html>, not <body>).
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
